import { MouseEvent, useCallback, useState } from 'react';
import {
  AiOutlineEye,
  AiOutlineEyeInvisible,
  AiOutlineCheckCircle,
} from 'react-icons/ai';
import { FiCopy } from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';

import { CodeLabel } from '@/components/CodeLabel';
import { TransparentButton } from './TransparentButton';
import { useCopy } from '@/components/useCopy';

let timeout: NodeJS.Timeout;

export const SecretLabel: React.FC<
  Omit<
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLSpanElement>,
      HTMLSpanElement
    >,
    'children'
  > & { children: string }
> = ({ children, onClick, ...props }) => {
  const [isHidden, setIsHidden] = useState(true);
  const [wasCopied, setWasCopied] = useState(false);
  const toggleIsHidden = useCallback(() => setIsHidden(value => !value), []);
  const copy = useCopy(children);

  const onClickWrapper = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      copy();
      setWasCopied(true);
      onClick?.(e);
      clearTimeout(timeout);
      timeout = setTimeout(() => setWasCopied(false), 2000);
    },
    [onClick, copy],
  );

  return (
    <>
      <span className="lg:hidden">
        <TransparentButton compact onClick={onClickWrapper}>
          <AnimatePresence mode="wait">
            {wasCopied ? (
              <motion.span
                key="check"
                initial={{ opacity: 0, scale: 0.2 }}
                animate={{
                  opacity: 1,
                  scale: 1.2,
                  transition: { duration: 0.2 },
                }}
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
              >
                <AiOutlineCheckCircle className="text-green-600" />
              </motion.span>
            ) : (
              <motion.span
                initial={{ opacity: 1 }}
                animate={{ opacity: 1, transition: { duration: 0.2 } }}
                exit={{ opacity: 0, transition: { duration: 0.1 } }}
                key="copy"
              >
                <FiCopy />
              </motion.span>
            )}
          </AnimatePresence>
        </TransparentButton>
      </span>

      <span className="hidden lg:flex items-center gap-x-2">
        <CodeLabel {...props} onClick={onClickWrapper} value={children}>
          <AnimatePresence mode="wait">
            <motion.div
              key={isHidden ? 'true' : 'false'}
              initial={{ opacity: 1 }}
              animate={{ opacity: 1, transition: { duration: 0.2 } }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
            >
              {isHidden ? [...children].map(() => '*') : children}
            </motion.div>
          </AnimatePresence>
        </CodeLabel>
        <AnimatePresence mode="wait">
          {wasCopied ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.2 }}
              animate={{
                opacity: 1,
                scale: 1.2,
                transition: { duration: 0.2 },
              }}
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
              key="check"
            >
              <AiOutlineCheckCircle className="text-green-600" />
            </motion.div>
          ) : (
            <motion.button
              initial={{ opacity: 1 }}
              animate={{ opacity: 1, transition: { duration: 0.2 } }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              key="eye"
            >
              {isHidden ? (
                <AiOutlineEye onClick={toggleIsHidden} />
              ) : (
                <AiOutlineEyeInvisible onClick={toggleIsHidden} />
              )}
            </motion.button>
          )}
        </AnimatePresence>
      </span>
    </>
  );
};
